import { useState } from 'react'
import { Field, Label, Input, Select, Button } from '@/components/ui'
import { useTranslations } from '@/lib/i18n'
import { humanize } from '@/lib/format'

export interface FilterValues {
  status?: string
  payment_type?: string
  currency?: string
  merchant_order_id?: string
  merchant_reference?: string
  created_at_min?: string
  created_at_max?: string
  [key: string]: string | undefined
}

export interface FilterOptions {
  statuses: string[]
  payment_types: { value: string; label: string }[]
  currencies: string[]
}

/**
 * Filter bar for payments/payouts. Maps directly to the gateway's searchable
 * params. Calls `onApply` with the (cleaned) filter set; the page turns that
 * into an Inertia GET.
 */
export function TransactionFilters({
  filters,
  options,
  onApply,
}: {
  filters: FilterValues
  options: FilterOptions
  onApply: (filters: FilterValues) => void
}) {
  const { t, locale } = useTranslations()
  const [draft, setDraft] = useState<FilterValues>(filters)
  // Native date inputs format per the element's lang. The product is CET /
  // European, so render day-first (en → en-GB) instead of the US default.
  const dateLang = locale === 'en' ? 'en-GB' : locale
  // Keep a persisted currency that isn't in the preset list selectable.
  const currencyOptions = [...new Set([...options.currencies, draft.currency].filter(Boolean) as string[])]

  function set(key: keyof FilterValues, value: string) {
    setDraft((prev) => ({ ...prev, [key]: value }))
  }

  function submit(event: React.FormEvent) {
    event.preventDefault()
    const cleaned = Object.fromEntries(Object.entries(draft).filter(([, v]) => v)) as FilterValues
    onApply(cleaned)
  }

  function clear() {
    setDraft({})
    onApply({})
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field>
          <Label>{t('payments.filters.status')}</Label>
          <Select value={draft.status ?? ''} onChange={(e) => set('status', e.target.value)}>
            <option value="">{t('payments.filters.any')}</option>
            {options.statuses.map((status) => (
              <option key={status} value={status}>
                {humanize(status)}
              </option>
            ))}
          </Select>
        </Field>

        <Field>
          <Label>{t('payments.filters.method')}</Label>
          <Select value={draft.payment_type ?? ''} onChange={(e) => set('payment_type', e.target.value)}>
            <option value="">{t('payments.filters.any')}</option>
            {options.payment_types.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field>
          <Label>{t('payments.filters.currency')}</Label>
          <Select value={draft.currency ?? ''} onChange={(e) => set('currency', e.target.value)}>
            <option value="">{t('payments.filters.any')}</option>
            {currencyOptions.map((currency) => (
              <option key={currency} value={currency}>
                {currency}
              </option>
            ))}
          </Select>
        </Field>

        <Field>
          <Label>{t('payments.filters.order_id')}</Label>
          <Input value={draft.merchant_order_id ?? ''} onChange={(e) => set('merchant_order_id', e.target.value)} />
        </Field>

        <Field>
          <Label>{t('payments.filters.reference')}</Label>
          <Input value={draft.merchant_reference ?? ''} onChange={(e) => set('merchant_reference', e.target.value)} />
        </Field>

        <Field>
          <Label>{t('payments.filters.from')}</Label>
          <Input lang={dateLang} type="date" value={draft.created_at_min ?? ''} onChange={(e) => set('created_at_min', e.target.value)} />
        </Field>

        <Field>
          <Label>{t('payments.filters.to')}</Label>
          <Input lang={dateLang} type="date" value={draft.created_at_max ?? ''} onChange={(e) => set('created_at_max', e.target.value)} />
        </Field>
      </div>

      <div className="flex gap-2">
        <Button type="submit" color="brand">
          {t('payments.filters.apply')}
        </Button>
        <Button type="button" plain onClick={clear}>
          {t('payments.filters.clear')}
        </Button>
      </div>
    </form>
  )
}
